import { ModuleValueGeneral, SchemaModuleGeneral } from './module-general';
import { JSONSchema } from '../types/json-schema';
import { FormComponent } from '../form.component';

import * as rawJSONSchemaModuleBariatric from '../../schemas/module-bariatric.json';

const LBS_PER_KG = 2.20462;
const CM_PER_IN = 2.54;

class ModuleValueBariatric extends ModuleValueGeneral {
  target_body_weight?: number;
  target_body_weight_imperial?: number;
  target_bmi?: number;
  excess_weight?: number;
  excess_weight_imperial?: number;
  excess_bmi?: number;
  waist_circumference?: number;
  waist_circumference_imperial?: number;
  hip_circumference?: number;
  hip_circumference_imperial?: number;
  waist_hip_ratio?: number;
}

export class SchemaModuleBariatric extends SchemaModuleGeneral<ModuleValueBariatric> {
  schema: JSONSchema<ModuleValueBariatric>;

  private _converting: Record<string, boolean> = {};
  private _conversionTimeout: Record<string, any> = {};

  constructor() {
    super();

    this.loadSchema( rawJSONSchemaModuleBariatric );
  }

  hook( form: FormComponent ) {
    super.hook( form );

    this.listenValue( [ 'target_bmi' ], this.calculateBodyWeightOnTargetBmi.bind( this ) );
    this.listenValue( [ 'bmi', 'target_bmi' ], this.fieldHandlerBmiTargetBmi.bind( this ) );
    this.listenValue( [ 'target_body_weight', 'body_height' ], this.fieldHandlerTargetBodyWeightBodyHeight.bind( this ) );
    this.listenValue( [ 'body_weight', 'target_body_weight' ], this.fieldHandlerBodyWeightTargetBodyWeight.bind( this ) );
    this.listenValue( [ 'waist_circumference', 'hip_circumference' ], this.calculateWaistHipRatio.bind( this ) );

    // The calculations above all run on the metric fields (kg/cm), which are hidden in
    // imperial mode. Keep each metric/imperial pair in sync — the canonical metric value
    // drives the calculations, the imperial value is for entry/display in imperial mode.

    // Imperial to Metric (active only in imperial mode, when the imperial field is shown)
    this.listenValue( 'target_body_weight_imperial', ( v: number ) => this.convertUnit( v, 'target_body_weight', 'target_weight', 1 / LBS_PER_KG, false ) );
    this.listenValue( 'waist_circumference_imperial', ( v: number ) => this.convertUnit( v, 'waist_circumference', 'waist', CM_PER_IN, false ) );
    this.listenValue( 'hip_circumference_imperial', ( v: number ) => this.convertUnit( v, 'hip_circumference', 'hip', CM_PER_IN, false ) );

    // Metric to Imperial (active only in metric mode, when the metric field is shown)
    this.listenValue( 'target_body_weight', ( v: number ) => this.convertUnit( v, 'target_body_weight_imperial', 'target_weight', LBS_PER_KG, true ) );
    this.listenValue( 'waist_circumference', ( v: number ) => this.convertUnit( v, 'waist_circumference_imperial', 'waist', 1 / CM_PER_IN, true ) );
    this.listenValue( 'hip_circumference', ( v: number ) => this.convertUnit( v, 'hip_circumference_imperial', 'hip', 1 / CM_PER_IN, true ) );

    // Excess Weight is always computed in kg; mirror it into the imperial (lbs) display field.
    this.listenValue( 'excess_weight', this.fieldHandlerExcessWeightImperial.bind( this ) );
  }

  fieldHandlerTargetBodyWeightBodyHeight() {
    // Read via getValue, not the listenValue args: in imperial mode body_height (and
    // target_body_weight) are hidden, so the visibility-checked args arrive undefined
    // and would wipe Target BMI. The canonical kg/cm values are always populated (kept
    // in sync from the imperial fields), so Target BMI is computed in both unit modes.
    const target_body_weight_kg = this.getValue( 'target_body_weight' );
    const height_cm = this.getValue( 'body_height' );

    if ( target_body_weight_kg && height_cm ) {
      this.calculateTargetBMI( target_body_weight_kg, height_cm );
    }
  }

  calculateTargetBMI( target_body_weight_kg: number, height_cm: number ) {
    let targetBmi = null;
    const height_m = height_cm / 100;

    const targetBmiSchema = this.getProperty( 'target_bmi' ).schema as JSONSchema;

    // Clear previous message
    targetBmiSchema.description = '';

    if ( target_body_weight_kg === null && height_cm === null ) {
      // No values

    } else if ( target_body_weight_kg === null || height_cm === null ) {
      // Show info message about partial completion
      targetBmiSchema.description = this.generateMessage( '%0 calculation requires both %1 and %2', 'target_bmi', 'target_body_weight', 'body_height' );

    } else if ( target_body_weight_kg > 0 && height_m > 0 ) {
      // Calculate the BMI
      targetBmi = Math.round( target_body_weight_kg / ( height_m ** 2 ) );
      if ( isNaN( targetBmi ) || !isFinite( targetBmi ) ) {
        targetBmi = null;
      }

      // Show out-of-range messages
      if ( targetBmi < 18.5 ) {
        targetBmiSchema.description = 'Low';
      } else if ( targetBmi > 30 ) {
        targetBmiSchema.description = 'Very high';
      } else if ( targetBmi > 25 ) {
        targetBmiSchema.description = 'High';
      }
    }

    this.updateValue( 'target_bmi', targetBmi );
  }

  fieldHandlerBodyWeightTargetBodyWeight() {
    // body_weight is always stored in kg — in imperial mode the field is hidden
    // but kept in sync from body_weight_imperial by fieldHandlerImperialWeightToMetric
    // (module-general). Read it via getValue, not the visibility-checked listenValue
    // args (which arrive undefined while the metric field is hidden), so Excess
    // Weight is correctly computed from the kg value in both metric and imperial modes.
    const body_weight_kg = this.getValue( 'body_weight' );
    const target_body_weight_kg = this.getValue( 'target_body_weight' );

    if ( body_weight_kg && target_body_weight_kg ) {
      this.updateValue( 'excess_weight', body_weight_kg - target_body_weight_kg );
    } else {
      this.updateValue( 'excess_weight', null );
    }
  }

  fieldHandlerBmiTargetBmi() {
    const bmi = this.getValue( 'bmi' );
    const target_bmi = this.getValue( 'target_bmi' );

    if ( bmi && target_bmi ) {
      this.updateValue( 'excess_bmi', bmi - target_bmi );
    } else {
      this.updateValue( 'excess_bmi', null );
    }
  }

  /**
   * Calculate the target body weight based on the target BMI
   * @param target_bmi
   */
  calculateBodyWeightOnTargetBmi ( target_bmi: number ) {
    if ( target_bmi && this.getValue( 'body_height' ) ) {
      const height_m = this.getValue( 'body_height' ) / 100;

      let targetWeight = Math.round( target_bmi * ( height_m ** 2 ) );

      this.updateValue( 'target_body_weight', targetWeight );
    }
  }

  /**
   * Calculate the waist-hip ratio
   * @param waist_cm
   * @param hip_cm
   */
  calculateWaistHipRatio() {
    // Read via getValue (not the visibility-checked listenValue args): in imperial mode
    // the cm fields are hidden but kept in sync from the inch fields, so the ratio stays
    // correct when the user edits waist/hip in either unit mode.
    const waist_cm = this.getValue( 'waist_circumference' );
    const hip_cm = this.getValue( 'hip_circumference' );

    if ( waist_cm && hip_cm ) {
      this.updateValue( 'waist_hip_ratio', parseFloat( (waist_cm / hip_cm).toFixed(2) ) );
    }
  }

  /**
   * Mirror the computed Excess Weight (kg) into its imperial (lbs) display field.
   */
  fieldHandlerExcessWeightImperial() {
    const excess_kg = this.getValue( 'excess_weight' );

    if ( excess_kg !== null && excess_kg !== undefined ) {
      this.updateValue( 'excess_weight_imperial', Math.round( excess_kg * LBS_PER_KG * 10 ) / 10 );
    } else {
      this.updateValue( 'excess_weight_imperial', null );
    }
  }

  /**
   * Convert a value between a metric/imperial field pair, keeping the two in sync.
   * Debounced to handle rapid typing and guarded with a per-pair flag to avoid feedback
   * loops. Runs only in the relevant unit mode, so editing the visible field updates the
   * hidden counterpart without the reverse handler firing back.
   *
   * @param value             New value of the source (listened) field
   * @param toField           Destination field to update
   * @param pairKey           Shared key identifying the metric/imperial pair
   * @param factor            Multiplier converting source units to destination units
   * @param activeInMetricMode Whether this direction runs in metric mode (true) or imperial mode (false)
   */
  convertUnit( value: number, toField: string, pairKey: string, factor: number, activeInMetricMode: boolean ) {
    if ( value === undefined || value === null || this._converting[ pairKey ] ) {
      return;
    }

    // Only the direction matching the active unit mode should run.
    if ( !!this.getValue( 'is_metric' ) !== activeInMetricMode ) {
      return;
    }

    const timeoutKey = `${pairKey}:${toField}`;
    if ( this._conversionTimeout[ timeoutKey ] ) {
      clearTimeout( this._conversionTimeout[ timeoutKey ] );
    }

    this._conversionTimeout[ timeoutKey ] = setTimeout( () => {
      this._converting[ pairKey ] = true;

      if ( value > 0 ) {
        const converted = Math.round( value * factor * 10 ) / 10; // Round to 1 decimal place
        if ( this.getValue( toField as any ) !== converted ) {
          this.updateValue( toField as any, converted );
        }
      } else if ( this.getValue( toField as any ) !== null ) {
        this.updateValue( toField as any, null );
      }

      this._converting[ pairKey ] = false;
    }, 300 );
  }

}



// WEBPACK FOOTER //
// /var/www/schemas-forms/src/app/forms/module-bariatric.ts